import { type CSSVariablesResolver, createTheme, type MantineColorsTuple } from '@mantine/core';

/**
 * Motyw aplikacji biurowej: kolor firmowy (zieleń-turkus, „eko”), krój Inter, jasne tło z białymi kartami,
 * łagodne zaokrąglenia. Wartości domyślne komponentów — tak, by strony nie musiały powtarzać stylów.
 */
const eko: MantineColorsTuple = ['#e8f8f3', '#d4efe7', '#a8dece', '#79ccb3', '#53bd9c', '#3bb38e', '#2aa785', '#1a9172', '#0b7d62', '#006a52'];

const font = "'Inter Variable', Inter, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

export const theme = createTheme({
  primaryColor: 'eko',
  primaryShade: { light: 8, dark: 6 },
  colors: { eko },
  defaultRadius: 'md',
  fontFamily: font,
  fontFamilyMonospace: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  headings: { fontFamily: font, fontWeight: '700', sizes: { h1: { fontSize: '28px', lineHeight: '1.25' }, h2: { fontSize: '24px', lineHeight: '1.3' }, h3: { fontSize: '18px', lineHeight: '1.35' }, h4: { fontSize: '16px' }, h5: { fontSize: '14px' } } },
  cursorType: 'pointer',
  focusRing: 'auto',
  shadows: {
    xs: '0 1px 2px rgba(16, 24, 40, 0.05)',
    sm: '0 1px 3px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(16, 24, 40, 0.04)',
    md: '0 4px 12px rgba(16, 24, 40, 0.08)',
    lg: '0 12px 32px rgba(16, 24, 40, 0.12)',
  },
  components: {
    Card: { defaultProps: { radius: 'lg', withBorder: true, shadow: 'xs' } },
    Paper: { defaultProps: { radius: 'lg' } },
    Button: { defaultProps: { radius: 'md' }, styles: { label: { fontWeight: 600 } } },
    ActionIcon: { defaultProps: { radius: 'md' } },
    Badge: { defaultProps: { radius: 'sm' }, styles: { root: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 } } },
    Modal: { defaultProps: { radius: 'lg', overlayProps: { backgroundOpacity: 0.4, blur: 2 } }, styles: { title: { fontWeight: 700, fontSize: 'var(--mantine-font-size-lg)' } } },
    Drawer: { defaultProps: { overlayProps: { backgroundOpacity: 0.3, blur: 2 } } },
    Menu: { defaultProps: { radius: 'md', shadow: 'md' } },
    Popover: { defaultProps: { radius: 'md', shadow: 'md' } },
    Tooltip: { defaultProps: { radius: 'sm', withArrow: true } },
    TextInput: { defaultProps: { radius: 'md' } },
    Select: { defaultProps: { radius: 'md' } },
    MultiSelect: { defaultProps: { radius: 'md' } },
    Textarea: { defaultProps: { radius: 'md' } },
    NumberInput: { defaultProps: { radius: 'md' } },
    PasswordInput: { defaultProps: { radius: 'md' } },
    SegmentedControl: { defaultProps: { radius: 'md' } },
    Tabs: { styles: { tab: { fontWeight: 500 } } },
    Table: { defaultProps: { verticalSpacing: 'sm', horizontalSpacing: 'md' } },
    Title: { styles: { root: { letterSpacing: '-0.015em' } } },
    NavLink: { styles: { root: { borderRadius: 'var(--mantine-radius-md)' } } },
    Notification: { defaultProps: { radius: 'md' } },
    Alert: { defaultProps: { radius: 'md' } },
  },
});

/**
 * Zmienne aplikacji (tło, obramowania, siatki planowania, odcienie) — jasny i ciemny wariant.
 * W jasnym motywie wartości odpowiadają dotychczasowym kolorom Mantine (gray-0…3, blue-0/1 itd.).
 */
export const cssResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--app-bg': '#f5f7f9',
    '--app-surface': '#ffffff',
    '--app-border': '#e6e9ee',
    '--app-muted': '#f1f3f6',
    '--app-nav-active': 'var(--mantine-color-eko-0)',
    '--app-highlight': 'var(--mantine-color-eko-0)',
    '--app-line': 'var(--mantine-color-gray-2)',
    '--app-line-strong': 'var(--mantine-color-gray-3)',
    '--app-subtle': 'var(--mantine-color-gray-0)',
    '--app-past': 'var(--mantine-color-gray-1)',
    '--app-today': 'var(--mantine-color-blue-0)',
    '--app-today-strong': 'var(--mantine-color-blue-1)',
    '--app-absence': '#ffe3e3',
    '--app-absence-request': '#fff3bf',
    '--app-chip-event': '#e5dbff',
    '--app-chip-entry': '#e9ecef',
    '--app-chip-text': 'var(--mantine-color-dark-7)',
    '--app-tint-indigo': 'var(--mantine-color-indigo-0)',
    '--app-tint-yellow': 'var(--mantine-color-yellow-0)',
    '--app-tint-orange': 'var(--mantine-color-orange-1)',
    '--app-link': 'var(--mantine-color-eko-8)',
    '--app-text-blue': 'var(--mantine-color-blue-8)',
    '--app-text-orange': 'var(--mantine-color-orange-8)',
    '--app-text-orange-strong': 'var(--mantine-color-orange-9)',
    '--app-text-yellow': 'var(--mantine-color-yellow-8)',
    // tekst w kolorze (np. podpowiedź „Co teraz?”) — w ciemnym motywie jaśniejsze odcienie (kontrast ≥ 4,5:1 na #1f2229)
    '--app-text-eko': 'var(--mantine-color-eko-8)',
    '--app-text-red': 'var(--mantine-color-red-7)',
    // wykresy zestawień: seria 1 (umowy) i 2 (wpływy) — sprawdzone pod kątem daltonizmu i kontrastu (dataviz validate_palette)
    '--chart-1': '#1a9172',
    '--chart-2': '#5b6fd6',
    '--chart-grid': '#e6e9ee',
  },
  dark: {
    '--app-bg': '#16181d',
    '--app-surface': '#1f2229',
    '--app-border': '#2c3038',
    '--app-muted': '#25282f',
    '--app-nav-active': 'rgba(42, 167, 133, 0.16)',
    '--app-highlight': 'rgba(42, 167, 133, 0.12)',
    '--app-line': '#2e323a',
    '--app-line-strong': '#3a3e47',
    '--app-subtle': 'rgba(255, 255, 255, 0.03)',
    '--app-past': 'rgba(0, 0, 0, 0.22)',
    '--app-today': 'rgba(34, 139, 230, 0.12)',
    '--app-today-strong': 'rgba(34, 139, 230, 0.24)',
    '--app-absence': 'rgba(250, 82, 82, 0.3)',
    '--app-absence-request': 'rgba(250, 176, 5, 0.3)',
    '--app-chip-event': 'rgba(132, 94, 247, 0.32)',
    '--app-chip-entry': '#3a3e47',
    '--app-chip-text': 'var(--mantine-color-dark-0)',
    '--app-tint-indigo': 'rgba(76, 110, 245, 0.12)',
    '--app-tint-yellow': 'rgba(250, 176, 5, 0.1)',
    '--app-tint-orange': 'rgba(253, 126, 20, 0.18)',
    '--app-link': 'var(--mantine-color-eko-4)',
    '--app-text-blue': 'var(--mantine-color-blue-4)',
    '--app-text-orange': 'var(--mantine-color-orange-4)',
    '--app-text-orange-strong': 'var(--mantine-color-orange-3)',
    '--app-text-yellow': 'var(--mantine-color-yellow-4)',
    '--app-text-eko': 'var(--mantine-color-eko-4)',
    '--app-text-red': 'var(--mantine-color-red-4)',
    '--chart-1': '#2aa785',
    '--chart-2': '#7b8cec',
    '--chart-grid': '#2e323a',
  },
});
